import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useCartStore } from '../../store/cart';
import { createOrder, createRazorpayOrder, verifyPayment } from '../../api/orders';
import styles from './CheckoutPage.module.css';

declare global {
  interface Window {
    Razorpay: new (opts: Record<string, unknown>) => { open(): void };
  }
}

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, total, clearCart } = useCartStore();
  const [loading, setLoading] = useState(false);

  const subtotal = total();
  const shippingCharge = subtotal >= 999 ? 0 : 99;
  const orderTotal = subtotal + shippingCharge;

  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    addressLine1: '', addressLine2: '',
    city: '', state: 'Tamil Nadu', pincode: '',
  });

  const setField = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!items.length) return toast.error('Cart is empty');
    setLoading(true);

    try {
      // 1. Create order in DynamoDB
      const { orderId } = await createOrder({
        items: items.map(i => ({ productId: i.productId, quantity: i.quantity })),
        shippingAddress: form,
      });

      // 2. Create Razorpay order
      const rzp = await createRazorpayOrder(orderId);

      // 3. Load Razorpay script if not loaded
      if (!window.Razorpay) {
        await new Promise<void>((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = () => res();
          s.onerror = () => rej(new Error('Razorpay script failed'));
          document.head.appendChild(s);
        });
      }

      // 4. Open Razorpay checkout
      const rzpInstance = new window.Razorpay({
        key: rzp.keyId,
        amount: rzp.amount,
        currency: rzp.currency,
        name: 'Sekar Silks',
        description: `Order #${orderId.slice(0, 8).toUpperCase()}`,
        order_id: rzp.razorpayOrderId,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: '#8B1A1A' },
        handler: async (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            await verifyPayment({
              orderId,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            clearCart();
            navigate(`/orders/confirmation/${orderId}`);
          } catch {
            toast.error('Payment verification failed. Contact support.');
          }
        },
      });
      rzpInstance.open();
    } catch (err) {
      toast.error('Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('checkout.title')}</h1>
      <div className={styles.layout}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <h2 className={styles.sectionTitle}>{t('checkout.shipping_address')}</h2>
          {[
            { key: 'name', label: t('checkout.name'), required: true },
            { key: 'phone', label: t('checkout.phone'), required: true, type: 'tel' },
            { key: 'email', label: 'Email (for confirmation)', required: false, type: 'email' },
            { key: 'addressLine1', label: t('checkout.address_line1'), required: true },
            { key: 'addressLine2', label: t('checkout.address_line2'), required: false },
            { key: 'city', label: t('checkout.city'), required: true },
            { key: 'pincode', label: t('checkout.pincode'), required: true },
          ].map(({ key, label, required, type }) => (
            <div key={key} className={styles.field}>
              <label className={styles.label}>{label}{required && ' *'}</label>
              <input
                type={type || 'text'}
                value={(form as Record<string, string>)[key]}
                onChange={setField(key)}
                required={required}
                className={styles.input}
              />
            </div>
          ))}
          <div className={styles.field}>
            <label className={styles.label}>{t('checkout.state')} *</label>
            <input value={form.state} onChange={setField('state')} required className={styles.input} />
          </div>
          <button type="submit" className={styles.payBtn} disabled={loading}>
            {loading ? 'Processing...' : `${t('checkout.pay_now')} — ₹${orderTotal.toLocaleString('en-IN')}`}
          </button>
        </form>

        <div className={styles.summary}>
          <h2 className={styles.sectionTitle}>{t('checkout.order_summary')}</h2>
          {items.map(item => (
            <div key={item.productId} className={styles.summaryItem}>
              <span className={styles.summaryName}>{item.name} × {item.quantity}</span>
              <span>₹{(item.price * item.quantity).toLocaleString('en-IN')}</span>
            </div>
          ))}
          <div className={styles.summaryRow}><span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
          <div className={styles.summaryRow}><span>Shipping</span><span>{shippingCharge === 0 ? 'Free' : `₹${shippingCharge}`}</span></div>
          <div className={`${styles.summaryRow} ${styles.totalRow}`}><span>Total</span><span>₹{orderTotal.toLocaleString('en-IN')}</span></div>
        </div>
      </div>
    </div>
  );
}
